/**
 * Session handling against self-hosted GoTrue.
 *
 * There is no sign-up path, deliberately: `GOTRUE_DISABLE_SIGNUP=true` on the server and
 * no form for it here. Accounts are created by an admin who sends an invite. An operator
 * console that lets anyone self-register into a tenant has no tenancy at all.
 *
 * `mustChangePassword` is read from app metadata and is enforced here rather than
 * server-side-only, because a seeded or invited password is a SHARED credential until it
 * is changed — and a shared credential makes the audit trail meaningless, since every
 * action attributed to a user could have been anyone who saw the invite.
 */
import type { Session, User } from '@supabase/supabase-js';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  setSessionProvider,
  setSessionRefresher,
  setUnauthorizedHandler,
} from './api/client';
import { readAuthLink } from '@/modules/auth/auth-fragment';
import { api } from './api/client';
import { humanError } from '@/lib/human-error';
import { AuthContext, type AuthState } from './auth-context';
import { supabase } from './supabase';

/**
 * The live session, held at module scope and updated SYNCHRONOUSLY with the state that
 * drives rendering.
 *
 * This is not a convenience. Registering the token getter from a `useEffect` looks
 * correct and is not: React flushes child effects before parent ones, so the queries
 * inside the app tree mount and issue their first request BEFORE the provider's effect
 * runs. The getter still returns the previous value — `null` on the first render after a
 * successful login — and every initial request goes out with no Authorization header.
 *
 * The symptom is nasty to read: a valid session, a 401 on every panel, and no
 * verification error server-side because the header was never there. The API's own
 * 401 handler then signs the user out, so the app bounces straight back to the login
 * page having apparently accepted the password.
 */
let currentSession: Session | null = null;

// Registered once, at module load, so it is in place before any component renders.
setSessionProvider(() => currentSession);

/**
 * How the API client recovers a stale access token.
 *
 * Registered at module load, beside the session getter, because the FIRST request a cold tab
 * makes can already be the one that needs it — a tab restored after an hour asleep fires its
 * queries before any component has had a chance to register anything.
 *
 * `refreshSession()` redeems the refresh token and, on success, `onAuthStateChange` updates
 * `currentSession` — so the client's replay picks up the new token through the getter above
 * rather than being handed one here. One source of truth for "what token are we using".
 *
 * Returning false rather than throwing: the caller's job is to decide whether to replay or
 * to sign out, and an exception here would have to be caught there anyway.
 */
setSessionRefresher(async () => {
  const { data, error } = await supabase.auth.refreshSession();
  if (error || !data.session) return false;
  currentSession = data.session;
  return true;
});

function readMustChange(user: User | null): boolean {
  const meta = user?.app_metadata as { must_change_password?: unknown } | undefined;
  return meta?.must_change_password === true;
}

/**
 * Redeem the session an invite or recovery link arrived with.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * WHY THIS RUNS BEFORE `getSession()` RATHER THAN BESIDE IT
 * ───────────────────────────────────────────────────────────────────────────────
 *
 * `getSession()` reads persisted storage, and an invited user has nothing there — this is
 * their first ever visit. Ordering the redemption first means `loading` is still true while
 * the link is being exchanged, so the invited user never sees a flash of the sign-in form
 * for a password that has never been set. It also means the tokens the URL carried are
 * either in the session or discarded before anything renders.
 *
 * Guarded at module scope, not with a ref: React 19 mounts effects twice under StrictMode
 * and `setSession` is a network call. One redemption per page load, whatever React does.
 *
 * The tokens are passed straight to `setSession` and are never logged. GoTrue's error
 * message is safe to print — it never echoes the credential — but the credential itself
 * must not reach the console, an error string or an API call. See `auth-fragment.ts`.
 */
let linkRedeemed = false;

async function redeemAuthLink(): Promise<void> {
  const link = readAuthLink();
  if (!link || link.kind !== 'session' || linkRedeemed) return;
  linkRedeemed = true;

  const { error } = await supabase.auth.setSession({
    access_token: link.accessToken,
    refresh_token: link.refreshToken,
  });

  if (error) {
    /*
     * A link that GoTrue itself minted and now refuses is a clock skew, a restarted auth
     * container with a new JWT secret, or a token already spent by an email scanner
     * prefetching the URL. The user falls through to the sign-in frame, where the code
     * entry is the way out — so this is a diagnostic, not the user's error message.
     */
    console.warn('[auth] the session from the link was refused:', error.message);
  }
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    /*
     * getSession() reads the persisted token, so a reload does not bounce the operator
     * to the login page. onAuthStateChange then keeps it current, including the silent
     * refresh — without subscribing, the app keeps using a token that has expired and
     * every request starts failing with 401 for no visible reason.
     */
    void redeemAuthLink()
      .then(() => supabase.auth.getSession())
      .then(({ data }) => {
        if (!alive) return;
        currentSession = data.session;
        setSession(data.session);
        setLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      // Assigned before setState, so a re-render triggered by this update already sees
      // the new token through the getter above.
      currentSession = next;
      setSession(next);
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      /*
       * Reached only AFTER the client has already tried a refresh and a replay, so by here
       * the token really is dead — revoked, or GoTrue restarted with a new secret, or the
       * refresh token itself expired. Signing out locally is the only recovery: leaving the
       * stale session in place renders a logged-in shell where every panel is empty.
       *
       * It used to fire on the FIRST 401, which meant an idle tab whose scheduled refresh
       * had been throttled by the browser was signed out with a valid refresh token still in
       * storage. That is what read as "the session expires very quickly".
       */
      void supabase.auth.signOut();
    });
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error ? humanise(error.message) : null };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  /**
   * Set a password, THROUGH THE API.
   *
   * ───────────────────────────────────────────────────────────────────────────────
   * WHY NOT `supabase.auth.updateUser`, WHICH IS ONE LINE
   * ───────────────────────────────────────────────────────────────────────────────
   *
   * Because clearing the gate has to be the server's decision, and it was ours.
   *
   * This used to set the password directly and then write
   * `user_metadata: { must_change_password: false }`, with the gate reading
   * `app_metadata && user_metadata !== false`. The flag lives in app metadata precisely
   * because a user cannot write there — and honouring a user-metadata override handed that
   * property straight back: any gated operator could call
   * `supabase.auth.updateUser({ data: { must_change_password: false } })` from the console
   * and walk into the product with no password ever set. The gate was openable by the person
   * it was gating.
   *
   * `POST /me/password` sets the password and clears the app-metadata flag IN ONE GoTrue
   * request, and it decides for itself whether a first password may be set — from the
   * identity provider's own copy of the flag, never from anything this client claims. So the
   * only thing that opens the gate is actually setting a password.
   */
  const changePassword = useCallback(async (next: string) => {
    try {
      await api.post('/me/password', { newPassword: next });
    } catch (err) {
      /* The set-password screen renders this verbatim, and `ApiError.message` is built
         from the server's problem+json — the same channel that once put a request id and
         "name resolution failed" in front of a tenant administrator. */
      return { error: humanError(err, 'Could not set your password. Try again.') };
    }
    /*
     * ───────────────────────────────────────────────────────────────────────────────
     * SIGN IN AGAIN — DO NOT REFRESH. GOTRUE HAS JUST DESTROYED THE REFRESH TOKEN.
     * ───────────────────────────────────────────────────────────────────────────────
     *
     * The gate lives in the JWT's claims, so the token in hand still says the password is
     * owed and something has to fetch the server's new answer. The obvious move is
     * `refreshSession()`, and it is wrong: GoTrue REVOKES every refresh token for a user
     * when their password changes — correctly, since that is how you evict a session opened
     * with the old credential.
     *
     * Measured on the account this shipped against: `select count(*) from
     * auth.refresh_tokens` for that user went to 0 at the moment of the change, and
     * `refreshSession()` then answered `Invalid Refresh Token: Refresh Token Not Found`.
     * The password change had ALREADY SUCCEEDED — flag cleared, audit row written — and the
     * screen reported a failure on top of it, leaving the operator staring at a red error
     * having done everything right, with no way forward and no way to know they were
     * already through.
     *
     * So: mint a new session with the credential they have this second typed. It is the one
     * moment in the product where the plaintext password is legitimately in hand, and it is
     * exactly what the server will now accept.
     */
    const email = currentSession?.user?.email;
    if (email !== undefined) {
      const { error } = await supabase.auth.signInWithPassword({ email, password: next });
      if (!error) return { error: null };
    }

    /*
     * Could not re-establish — a rate limit, or the stack went away between the two calls.
     * SIGN OUT rather than leave a dead session on screen: the password IS changed, so the
     * only honest thing is to say so and put them at the sign-in form where it will work.
     */
    await supabase.auth.signOut();
    return {
      error:
        'Your password was changed. The session could not be renewed automatically — sign in again with your new password.',
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      /* APP METADATA ALONE. The user-metadata override that used to sit here was a gate the
         gated user could open — see `changePassword`. */
      mustChangePassword: readMustChange(session?.user ?? null),
      signIn,
      signOut,
      changePassword,
    }),
    [session, loading, signIn, signOut, changePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * GoTrue's messages are accurate and unhelpful, and occasionally neither.
 *
 * "Invalid login credentials" is fine. "AuthApiError: Failed to fetch" tells an operator
 * nothing about the actual problem. And when something between the browser and GoTrue
 * answers instead — a proxy, a gateway — the message is that system's, not GoTrue's, and it
 * arrives here on its way to the screen.
 *
 * TWO THINGS THIS USED TO DO THAT IT NO LONGER DOES:
 *
 *   · It printed SUPABASE_URL at the reader. "Cannot reach the authentication service at
 *     https://apigw.…" names infrastructure to somebody who cannot act on it, in the one
 *     place an unauthenticated stranger is guaranteed to be looking.
 *   · It ended `return message` — so anything GoTrue or a proxy said reached the screen
 *     verbatim, which is exactly how a raw JSON body with a correlation id ended up in the
 *     console's UI on another screen.
 *
 * The known cases are still mapped, because "Incorrect email or password" is worth far more
 * than a generic failure. Everything else goes through the same prose rules the rest of the
 * app uses, and falls back to a sentence naming what to do.
 */
function humanise(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('failed to fetch') || m.includes('networkerror') || m.includes('load failed')) {
    /* No URL. The reader's next step is the same whether the gateway is down or their wifi
       is: try again, and tell somebody if it persists. */
    return 'Cannot reach the sign-in service. Check your connection and try again.';
  }
  if (m.includes('invalid login credentials')) return 'Incorrect email or password.';
  if (m.includes('email not confirmed')) return 'This account has not been confirmed yet.';
  if (m.includes('user not found')) return 'No account exists for that email address.';
  if (m.includes('email rate limit') || m.includes('over_email_send_rate_limit')) {
    return 'Too many emails have been sent to that address recently. Wait a minute and try again.';
  }
  if (m.includes('too many requests') || m.includes('rate limit')) {
    return 'Too many attempts. Wait a moment and try again.';
  }
  if (m.includes('password should be')) return message;
  if (m.includes('weak password')) return 'That password is too weak. Use at least 8 characters.';
  if (m.includes('same password') || m.includes('should be different')) {
    return 'That is already your password. Choose a different one.';
  }
  /* Anything unrecognised is sanitised rather than trusted. */
  return humanError(new Error(message), 'Sign-in failed. Try again.');
}
